import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { DeleteOutlined } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Tooltip } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';

import type { ApiError, StyleObj } from '../../@types';
import type { GgaFreeSpinHistoryRow, GgaFreeSpinsQueryParams } from '../../@types/casino';
import { useModal } from '../../contexts/ModalContext';
import { useToast } from '../../contexts/ToastContext';
import { getCasinoFreeSpinsAvailability, prepareGgaFreeSpinsFilters } from '../../helpers/casino';
import { useCasinoTableQuery } from '../../hooks/useCasinoTableQuery';
import { useToastErrorMessage } from '../../hooks/useToastErrorMessage';
import { useCasinoMarketplace, useDeleteGgaFreeSpin, useGgaFreeSpinsHistory } from '../../queries/casino';
import { createColumn } from '../../utils';
import ConfirmationModal from '../modals/ConfirmationModal';
import DateRangeSelect, { type DayjsTimestamp } from '../molecules/DateRangeSelect';
import RetryAlert from '../molecules/RetryAlert';
import GgaFreeSpinsSettingsForm from '../organisms/forms/GgaFreeSpinsSettingsForm';
import GoldGamesFreeSpinsTable from '../organisms/GoldGamesFreeSpinsTable';
import TableTemplate from '../templates/TableTemplate';

const DEFAULT_SORT_FIELD = 'id';
const DEFAULT_SORT_ORDER = 'desc';

const createDefaultDateRange = (): DayjsTimestamp => ({
  fromTimestamp: dayjs().startOf('month'),
  toTimestamp: dayjs(),
});

const createDefaultQueryParams = (date: DayjsTimestamp): GgaFreeSpinsQueryParams => ({
  page: 1,
  limit: 25,
  dateFrom: date.fromTimestamp.valueOf(),
  dateTo: date.toTimestamp.valueOf(),
  sortField: DEFAULT_SORT_FIELD,
  sortOrder: DEFAULT_SORT_ORDER,
});

const DEFAULT_VISIBLE_COLUMNS = {
  id: true,
  name: true,
  user: true,
  fs_quantity: true,
  fs_amount: true,
  message: true,
  date: true,
  addedby: true,
  status: true,
  options: true,
};

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: { xs: 'auto', md: 'calc(100% - 200px)' },
    width: '100%',
    minWidth: 0,
  },
};

export const GgaFreeSpinsPage = () => {
  const { openModal } = useModal();
  const { enqueueSnackbar } = useToast();
  const { handleErrorMessage } = useToastErrorMessage();
  const [date, setDate] = useState<DayjsTimestamp>(createDefaultDateRange);
  const { queryParams, paginationModel, updateQueryParams, changeQuery, handleSearch, handleSort, changeDateRange } =
    useCasinoTableQuery<GgaFreeSpinsQueryParams>({
      defaultQuery: () => createDefaultQueryParams(date),
      emptyFields: ['search'],
      emptyArrayFields: ['playerIds'],
      searchField: 'search',
      sortDefaults: {
        sortColumn: 'sortField',
        sortOrder: 'sortOrder',
        defaultColumn: DEFAULT_SORT_FIELD,
        defaultOrder: DEFAULT_SORT_ORDER,
      },
      prepareQuery: prepareGgaFreeSpinsFilters,
    });
  const { data, isFetching, isLoading, isError, refetch } = useGgaFreeSpinsHistory(queryParams);
  const deleteFreeSpin = useDeleteGgaFreeSpin();

  const openFreeSpinsSettings = useCallback(() => {
    openModal(<GgaFreeSpinsSettingsForm />, undefined, { dialogSize: 'wide' });
  }, [openModal]);

  const handleDelete = useCallback(
    (freeSpin: GgaFreeSpinHistoryRow) => {
      const freeSpinId = String(freeSpin.id);
      const confirmDelete = async () => {
        try {
          await deleteFreeSpin.mutateAsync({
            freeSpinId,
            userId: String(freeSpin.user),
            sdate: new Date(queryParams.dateFrom).toISOString(),
            edate: new Date(queryParams.dateTo).toISOString(),
          });
          enqueueSnackbar({ message: `Free spin ${freeSpinId} deleted.`, variant: 'success' });
        } catch (error) {
          handleErrorMessage(error as ApiError);
          throw error;
        }
      };

      openModal(
        <ConfirmationModal
          title={`Delete free spin ${freeSpinId}?`}
          actionButtonLabel='Delete'
          actionButtonClose='Cancel'
          handleConfirm={confirmDelete}
        />
      );
    },
    [deleteFreeSpin, enqueueSnackbar, handleErrorMessage, openModal, queryParams.dateFrom, queryParams.dateTo]
  );

  const columns = useMemo<GridColDef<GgaFreeSpinHistoryRow>[]>(
    () => [
      createColumn('id', 'ID', { minWidth: 100 }),
      createColumn('name', 'Game', { minWidth: 200, flex: 1 }),
      createColumn('user', 'Player', {
        minWidth: 160,
        valueGetter: (_value, row) => row.username ?? String(row.user),
      }),
      createColumn('fs_quantity', 'Quantity', { minWidth: 110, type: 'number' }),
      createColumn('fs_amount', 'Amount', {
        minWidth: 120,
        valueGetter: (_value, row) => (row.currency ? `${row.fs_amount} ${row.currency}` : row.fs_amount),
      }),
      createColumn('message', 'User Message', { minWidth: 220, flex: 1, sortable: false }),
      createColumn('date', 'Date Added', {
        minWidth: 180,
        valueGetter: (_value, row) => (row.date ? dayjs(row.date).format('DD/MM/YYYY HH:mm:ss') : ''),
      }),
      createColumn('addedby', 'Added By', { minWidth: 140 }),
      createColumn('status', 'Status', { minWidth: 110, sortable: false }),
      createColumn('options', 'Options', {
        minWidth: 100,
        sortable: false,
        disableExport: true,
        renderCell: ({ row }: GridRenderCellParams<GgaFreeSpinHistoryRow>) => (
          <Tooltip title='Delete free spin'>
            <IconButton aria-label={`Delete free spin ${row.id}`} color='primary' onClick={() => handleDelete(row)}>
              <DeleteOutlined />
            </IconButton>
          </Tooltip>
        ),
      }),
    ],
    [handleDelete]
  );

  const handleDateChange = useCallback(
    (newDate: DayjsTimestamp) => {
      setDate(newDate);
      changeDateRange({
        dateFrom: newDate.fromTimestamp.valueOf(),
        dateTo: newDate.toTimestamp.valueOf(),
      });
    },
    [changeDateRange]
  );

  return (
    <Box sx={styles.container}>
      {isError && (
        <Alert severity='error' action={<Button onClick={() => refetch()}>Retry</Button>}>
          Unable to load GGA free spins.
        </Alert>
      )}
      <TableTemplate
        rows={data?.items ?? []}
        columns={columns}
        loading={isFetching || isLoading}
        rowCount={data?.count ?? 0}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        changeQuery={changeQuery}
        showFilter
        paginationModel={paginationModel}
        pageSizeOptions={[25, 50, 100, 200, 500]}
        filterType='casino-free-spins'
        mobileCardHeaderItems={(row) => [
          { label: 'Player', value: row.username ?? String(row.user), flex: 1, align: 'left' },
          { label: 'Game', value: row.name, flex: 1, align: 'left' },
          { label: 'Status', value: row.status, flex: 1 },
        ]}
        customTableHeaderContent={
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            useFlexGap
            sx={{
              width: '100%',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              alignItems: { xs: 'stretch', md: 'center' },
            }}
          >
            <DateRangeSelect date={date} onDateChange={handleDateChange} defaultDateRangeOption='custom' />
            <Button variant='outlined' startIcon={<SettingsOutlinedIcon />} onClick={openFreeSpinsSettings}>
              Free Spins Settings
            </Button>
          </Stack>
        }
      />
    </Box>
  );
};

const CasinoFreeSpinsPage = () => {
  const marketplace = useCasinoMarketplace();
  if (marketplace.isLoading) return <CircularProgress aria-label='Loading marketplace settings' />;
  if (marketplace.isError)
    return <RetryAlert onRetry={marketplace.refetch}>Unable to load marketplace settings.</RetryAlert>;
  const availability = getCasinoFreeSpinsAvailability(marketplace.data);
  if (availability.status !== 'available') {
    return (
      <Alert severity='info'>
        {availability.status === 'configuration-required'
          ? 'Ask a super admin to choose the Free Spins Aggregator in Casino Marketplace settings.'
          : 'Free spins are not enabled for this marketplace.'}
      </Alert>
    );
  }
  return (
    <>
      {availability.aggregator === 'GGA' ? (
        <GgaFreeSpinsPage key={marketplace.data!._id} />
      ) : (
        <GoldGamesFreeSpinsTable
          key={marketplace.data!._id}
          marketplaceId={marketplace.data!._id}
          currency={marketplace.data?.currency ?? ''}
        />
      )}
    </>
  );
};
export default CasinoFreeSpinsPage;
